import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity, PrimaryMethod } from '@api/api/entity-schema';
import { ReadMore } from '@design/components/read-more/ReadMore';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { OverviewKeyValues } from '@terminal/modules/overview-key-values/OverviewKeyValues';
import clsx from 'clsx';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import styles from './About.module.scss';

export interface AboutProps {
  entity: Entity;
  isModal?: boolean;
  showMoreDescription?: boolean;
  variant?: 'default' | 'secondary';
  limit?: number;
}

const getAboutDescription = (entity: Entity, isFxPair: boolean) => {
  const source = isFxPair
    ? entity.name_full.trim() || entity.company_description || ''
    : (entity.company_description ?? '');
  const description = source.trim();
  if (!description) {
    return '';
  }
  if (entity.sub_class === AssetSubClassCode.EquityStock) {
    return source;
  }
  if (
    /^(?:NA|N\/A)$/i.test(description) ||
    description.toLowerCase() === entity.name.trim().toLowerCase()
  ) {
    return '';
  }
  return description;
};

const getAboutFallback = (entity: Entity) => {
  if (entity.sub_class === AssetSubClassCode.EcoCentralBank) {
    return i18next.t('widget:assetOverview.companyProfile.centralBankFallback');
  }
  if (
    [AssetSubClassCode.FiSov, AssetSubClassCode.FiInflationSov].includes(
      entity.sub_class
    ) &&
    entity.primary_method === PrimaryMethod.Yield
  ) {
    return i18next.t(
      'widget:assetOverview.companyProfile.sovereignYieldFallback'
    );
  }
  return '';
};

/**
 * @deprecated
 */
export const About = ({
  entity,
  isModal = false,
  showMoreDescription = false,
  variant = 'default',
  limit = 200,
}: AboutProps) => {
  const { t } = useTranslation('widget');
  const isDefaultVariant = variant === 'default';
  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const isFxPair =
    entity.sub_class === AssetSubClassCode.MajorCross ||
    entity.sub_class === AssetSubClassCode.GenericCross;
  const isCentralBankOrSovereign =
    entity.sub_class === AssetSubClassCode.EcoCentralBank ||
    entity.sub_class === AssetSubClassCode.FiSov ||
    entity.sub_class === AssetSubClassCode.FiInflationSov;
  const hasSeparateMetricHeading =
    isCentralBankOrSovereign ||
    [
      AssetSubClassCode.FutureElectricity,
      AssetSubClassCode.FutureEnergy,
      AssetSubClassCode.FutureFood,
      AssetSubClassCode.FutureGrains,
      AssetSubClassCode.FutureMetals,
      AssetSubClassCode.FutureSofts,
      AssetSubClassCode.CommodityIndex,
    ].includes(entity.sub_class);
  const description =
    getAboutDescription(entity, isFxPair) || getAboutFallback(entity);
  const hasDescription = !!description;
  const aboutName = isCentralBankOrSovereign
    ? entity.name_full.trim() || entity.name
    : entity.name;

  /* i18next-extract-disable-next-line */
  const sectorName = isStock
    ? t([
        `widget:assetOverview.companyProfile.sectorNames.${entity.gics.sector.code}`,
        'widget:assetOverview.companyProfile.sectorNames.notFound',
      ])
    : '';
  /* i18next-extract-disable-next-line */
  const industryName = isStock
    ? t([
        `widget:assetOverview.companyProfile.sectorNames.${entity.gics.industry_group.code}`,
        'widget:assetOverview.companyProfile.sectorNames.notFound',
      ])
    : '';

  if (!hasDescription) {
    return isDefaultVariant ? null : (
      <OverviewKeyValues entityTag={entity.tag} entity={entity} isStandalone />
    );
  }

  return (
    <div
      className={clsx(styles.aboutRoot, {
        [styles.secondaryVariant]: !isDefaultVariant,
      })}
      data-testid="about"
    >
      <div className={styles.contentContainer}>
        {!isFxPair && (
          <SectionTitle
            title={t('widget:assetOverview.companyProfile.about', {
              companyName: aboutName,
            })}
          />
        )}
        {isStock && (
          <div className={styles.sectorAndIndustryWrapper}>
            <div className={styles.subTitlesWrapper}>
              <span className={styles.label}>
                {t('widget:assetOverview.companyProfile.sector')}
              </span>
              <span className={styles.value}>{sectorName}</span>
            </div>
            <div className={styles.subTitlesWrapper}>
              <span className={styles.label}>
                {t('widget:assetOverview.companyProfile.subIndustry')}
              </span>
              <span className={styles.value}>{industryName}</span>
            </div>
          </div>
        )}
        <p
          className={clsx(styles.description, {
            [styles.isModal]: isModal,
            [styles.secondaryVariant]: !isDefaultVariant,
          })}
        >
          {showMoreDescription ? (
            <ReadMore limit={limit}>{description}</ReadMore>
          ) : (
            description
          )}
        </p>
        {!isDefaultVariant && (
          <OverviewKeyValues
            entityTag={entity.tag}
            entity={entity}
            isDefaultVariant={hasSeparateMetricHeading}
          />
        )}
      </div>
    </div>
  );
};
